/**
 * Shared business rules for Filing Info across role0 (Phase 1
 * FilingPrerequisites) and role1 (Phase 2 FilingInformation).
 *
 * The two roles render different subsets of the same field universe but
 * share an identical rule set (same qnames, same conditional-required
 * logic, same date-sequence constraints). Keep rules HERE only so both
 * consumers stay in sync.
 *
 * Consumers:
 *  - role0-000000-filing-prerequisites.component.ts (Phase 1 editor)
 *  - role0-filing-prerequisites.validator.ts (Phase 1 pure validator)
 *  - role1-010000-filing-info.component.ts (Phase 2 editor — to be revived)
 *  - role1-filing-info.validator.ts (Phase 2 pure validator — to be revived)
 *
 * Component paths use these to drive imperative
 * setupConditionalValidation() / setupFinancialYearDateValidation() wiring
 * against Angular FormControls. Pure validator paths use them to compute
 * the same answers from a flat sheetData snapshot for the listing /
 * dashboard cache-miss path.
 *
 * Modify rules HERE only — anywhere else risks the parity-check harness
 * throwing in dev because the two paths disagree.
 */

import { DEI } from '../../../constants/qnames';

export const FILING_INFO_FIELD_KEYS = {
  applicationOfSubmission: DEI.ApplicationOfSubmission,
  financialStatementsPreparation:
    DEI.DisclosureOfFinancialStatementsPreparationForCurrentSubmission,
  auditStatus: DEI.DisclosureOfFinancialStatementsAuditStatus,
  accountingStandard: DEI.BasisOfAccountingStandardsAppliedToPrepareFinancialStatements,
  currentYearStart: DEI.CompanyCurrentFinancialYearStartDate,
  currentYearEnd: DEI.CompanyCurrentFinancialYearEndDate,
  previousYearStart: DEI.CompanyPreviousFinancialYearStartDate,
  previousYearEnd: DEI.CompanyPreviousFinancialYearEndDate,
} as const;

/**
 * Conditional required rules.
 *
 * When the trigger field's current value matches any entry in `whenAnyOf`,
 * each field listed in `requireFields` becomes required IN ADDITION to the
 * static required set declared in the role's form.json. The component sets
 * up one valueChanges subscription per rule; the validator iterates the
 * same list against a flat snapshot.
 */
export interface FilingInfoConditionalRule {
  trigger: string;
  whenAnyOf: ReadonlyArray<string>;
  requireFields: ReadonlyArray<string>;
}

export const FILING_INFO_CONDITIONAL_RULES: ReadonlyArray<FilingInfoConditionalRule> = [
  {
    trigger: FILING_INFO_FIELD_KEYS.applicationOfSubmission,
    whenAnyOf: ['Rectification filing'],
    requireFields: [DEI.StatutoryDeclarationForRectification],
  },
  {
    trigger: FILING_INFO_FIELD_KEYS.applicationOfSubmission,
    whenAnyOf: ['Court order filing', 'Rectification filing with court order'],
    requireFields: [DEI.CourtOrderReferenceNumber],
  },
  {
    trigger: FILING_INFO_FIELD_KEYS.financialStatementsPreparation,
    whenAnyOf: ['Subsequent preparation of financial statements'],
    requireFields: [
      FILING_INFO_FIELD_KEYS.previousYearStart,
      FILING_INFO_FIELD_KEYS.previousYearEnd,
    ],
  },
  // [Not supported] "Unaudited" is out of supported scope for Filing Info,
  // so this conditional-required rule is unreachable. Re-enable only if
  // Field #14 ever exposes the Unaudited option.
  // See docs/sheet-specs/00_filing-prerequisites.md Field #15.
  // {
  //   trigger: FILING_INFO_FIELD_KEYS.auditStatus,
  //   whenAnyOf: ['Unaudited'],
  //   requireFields: ['ssmt-dei:AuditExemptionCategory'],
  // },
  // [Not supported] "Others" is out of supported scope for Filing Info
  // (Field #16 does not expose it for the supported Incorporated-in-Malaysia
  // / Private-company variant). Re-enable alongside Field #17 only if it is
  // ever adopted.
  // {
  //   trigger: FILING_INFO_FIELD_KEYS.accountingStandard,
  //   whenAnyOf: ['Others'],
  //   requireFields: ['ssmt-dei:DisclosureOfOtherAccountingStandardsApplied'],
  // },
];

/**
 * Static required-field derivation.
 *
 * A Filing Info field is **unconditionally required** when EITHER its
 * top-level `required === true` flag is set OR its `validators` array carries
 * a `{ name: 'required' }` entry with no `conditionalOn` property — and it is
 * NOT additionally a *conditional* required (`{ name: 'required', conditionalOn }`),
 * which is handled separately by FILING_INFO_CONDITIONAL_RULES.
 *
 * This used to be re-implemented inline in FOUR places (role0 + role1
 * component `buildForm()`, and role0 + role1 pure validators). role1's copies
 * were upgraded to honour the `validators:[{name:'required'}]` form (the same
 * latent bug originally fixed on Role 6 — e.g. `DescriptionOfFunctionalCurrency`
 * carries `required: false` + `validators:[{name:'required'}]`) but role0's two
 * copies were left on the narrow `required === true` check, so the two roles
 * silently disagreed on the same field's required-ness. Deriving the set from
 * this single shared function keeps all four paths aligned by construction.
 */
export interface RequiredDerivableControl {
  qname: string;
  required?: boolean;
  validators?: ReadonlyArray<{ name?: string; conditionalOn?: unknown }>;
}

export function hasUnconditionalRequiredViaValidators(
  control: RequiredDerivableControl
): boolean {
  return (
    control.validators?.some(
      (validator) => validator.name === 'required' && validator.conditionalOn === undefined
    ) ?? false
  );
}

export function hasConditionalRequirement(
  control: RequiredDerivableControl
): boolean {
  return (
    control.validators?.some(
      (validator) => validator.name === 'required' && validator.conditionalOn !== undefined
    ) ?? false
  );
}

/**
 * True when `buildForm()` should attach `Validators.required` to this control
 * (i.e. it is unconditionally required and not a conditional-only required).
 */
export function isUnconditionallyRequired(
  control: RequiredDerivableControl
): boolean {
  return (
    ((control.required ?? false) ||
      hasUnconditionalRequiredViaValidators(control)) &&
    !hasConditionalRequirement(control)
  );
}

/**
 * The set of qnames that are statically (unconditionally) required for a given
 * control list. Used by both pure validators' `STATIC_REQUIRED_KEYS`.
 */
export function deriveStaticRequiredKeys(
  controls: ReadonlyArray<RequiredDerivableControl>
): ReadonlySet<string> {
  return new Set(
    controls.filter(isUnconditionallyRequired).map((control) => control.qname)
  );
}

/**
 * A DISABLED field fires no validations — not `required`, not `pattern`.
 *
 * This mirrors Angular, not a product rule: `buildForm()` creates a control as
 * `{ value, disabled: control.ui.readonly }` and `setupFieldDisabling()` calls
 * `targetControl.disable()` when a `disabledWhen` rule matches. Angular excludes
 * every disabled control from `form.valid` **and** from `form.value` — so the
 * editor has never surfaced a required error on one. The pure validators used to
 * check them anyway (role1's header called `disabledWhen` "transparent to
 * required/pattern checks", which is true for the *validator array* but not for
 * the verdict), so the headless listing could publish a red ✗ for a field the
 * editor showed as satisfied.
 *
 * It became load-bearing when `ssmt-dei:TypeOfSubmission` lost its
 * `ui.defaultValue: "FS-MFRS"`: it is `readonly` (therefore disabled and
 * uneditable) and `required`, so with the backend now sending `null` it would
 * publish a permanently unfixable blocking error. The mandatory error belongs on
 * its DRIVER — `Basis of accounting standards…`, which is enabled and required —
 * exactly as role0's extracted-NULL-driver branch already does for filtered
 * dropdowns. See `deriveTypeOfSubmission` in `filing-form-state.service.ts`.
 *
 * `disabledWhen` is evaluated against the same flat `sheetData` snapshot the rest
 * of the validator reads, so the two paths agree without a FormGroup.
 */
export interface DisableDerivableControl {
  qname: string;
  ui?: {
    readonly?: boolean;
    disabledWhen?: { field: string; values: ReadonlyArray<string> };
    autoManageByTypeOfSubmission?: boolean;
  };
}

export function isControlDisabled(
  control: DisableDerivableControl,
  sheetData: Record<string, unknown>
): boolean {
  if (control.ui?.readonly) return true;
  // THIRD disabling mechanism, and the only one not visible in the schema as a
  // disable rule: `setupBankNegaraFieldManagement()` (role0) calls `.disable()`
  // on every `autoManageByTypeOfSubmission` control the moment it is shown, and
  // hides it while Type of Submission is empty. So it is ALWAYS either hidden or
  // disabled — never editable, never validated by the editor. Without this line
  // the headless verdict flags a `required` one the editor treats as satisfied,
  // which is exactly the ✗-the-editor-never-shows bug this predicate exists to
  // prevent. Only one control uses the flag today
  // (`ssmt:DisclosureOfWhetherCompanyRegulatedByBankNegaraMalaysiaAtFinancialYearEnd`),
  // and it became load-bearing when that field was made `required: true` on
  // 2026-08-06 to match spec Field #26.
  if (control.ui?.autoManageByTypeOfSubmission) return true;
  const rule = control.ui?.disabledWhen;
  if (!rule) return false;
  return rule.values.includes(sheetData[rule.field] as string);
}

/**
 * The qnames currently disabled for `sheetData` — subtract this from the
 * required set and skip it in the pattern loop.
 */
export function deriveDisabledKeys(
  controls: ReadonlyArray<DisableDerivableControl>,
  sheetData: Record<string, unknown>
): ReadonlySet<string> {
  return new Set(
    controls.filter((control) => isControlDisabled(control, sheetData)).map((control) => control.qname)
  );
}

/**
 * Cross-field date constraints. Each function takes the raw values (string
 * or Date) and returns a human-readable error message, or null when the
 * constraint passes (or cannot be evaluated because one side is missing).
 *
 * The component wraps these into Angular ValidatorFn instances; the pure
 * validator calls them directly. Both paths produce identical messages so
 * QA snapshots / user-facing copy stay aligned.
 */

function toDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = new Date(value as string);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export function checkCurrentRange(
  startValue: unknown,
  endValue: unknown
): string | null {
  const start = toDate(startValue);
  const end = toDate(endValue);
  if (!start || !end) return null;
  if (start > end) {
    return 'Current period start date must be earlier than or equal to current period end date.';
  }
  return null;
}

export function checkPreviousRange(
  startValue: unknown,
  endValue: unknown
): string | null {
  const start = toDate(startValue);
  const end = toDate(endValue);
  if (!start || !end) return null;
  if (start > end) {
    return 'Previous period start date must be earlier than or equal to previous period end date.';
  }
  return null;
}

export function checkDateSequence(
  previousEndValue: unknown,
  currentStartValue: unknown
): string | null {
  const previousEnd = toDate(previousEndValue);
  const currentStart = toDate(currentStartValue);
  if (!previousEnd || !currentStart) return null;
  if (previousEnd >= currentStart) {
    return 'Previous period end date must be earlier than current period start date.';
  }
  return null;
}

/**
 * Earliest financial-year END date the MBRS filing flow supports.
 *
 * SSM's own preparation tool refuses a `CompanyCurrentFinancialYearEndDate`
 * earlier than 2017-01-31 (`ValidFromDate` in its `iFile.dll.config`; assertion
 * `Mandatory-dei-fs-mfrs-25`). Periods ending before that date belong to the
 * pre-Companies-Act-2016 regime and must be filed through the separate
 * "Financial Statements 1965" submission, which this application does not
 * produce.
 *
 * Enforced in three places, all reading THIS constant:
 *  - the calendar pickers on role0 / role1 (`[minDate]`, greying the cells out),
 *  - the editor's Angular validator (catches a TYPED date, which `minDate`
 *    does not block),
 *  - the pure validators, so the listing / dashboard verdict agrees with the
 *    open sheet.
 */
export const MIN_SUPPORTED_CURRENT_FINANCIAL_YEAR_END = new Date(2017, 0, 31);

/** Same floor as a `YYYYMMDD` integer, for timezone-free day comparison. */
const MIN_SUPPORTED_CURRENT_FINANCIAL_YEAR_END_DAY = 20170131;

/**
 * User-facing copy for a financial year end before the supported floor.
 *
 * Wording is the P.O.'s own, finalised 2026-09-15 — it superseded the
 * placeholder this shipped with. Treat it as copy owned by Product: reword only
 * on their say-so, and keep it here rather than inlining it, because the inline
 * error and the calendar tooltip must read identically.
 */
export const UNSUPPORTED_CURRENT_FINANCIAL_YEAR_END_MESSAGE =
  "Company's current financial year end date should be more than or equal to " +
  '31 January 2017. A financial period ending before that date must be filed ' +
  'as "Financial Statements 1965" instead.';

/**
 * Reduce a date value to a `YYYYMMDD` integer for CALENDAR-DAY comparison.
 *
 * Deliberately not `toDate()`: an ISO `yyyy-MM-dd` string parses as UTC
 * midnight while a picker-produced `Date` is local midnight, so comparing the
 * two as instants shifts the boundary by a day in some timezones. Comparing
 * day numbers makes the floor mean the same calendar date everywhere.
 */
function toCalendarDay(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'string') {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) return Number(iso[1]) * 10000 + Number(iso[2]) * 100 + Number(iso[3]);
  }

  const parsed = value instanceof Date ? value : new Date(value as string);
  if (isNaN(parsed.getTime())) return null;
  return parsed.getFullYear() * 10000 + (parsed.getMonth() + 1) * 100 + parsed.getDate();
}

/**
 * True when `value` names a calendar day earlier than the supported floor.
 *
 * Used by the templates to decide whether a greyed-out calendar cell should
 * carry the explanatory tooltip.
 */
export function isBeforeSupportedFinancialYearEnd(value: unknown): boolean {
  const day = toCalendarDay(value);
  return day !== null && day < MIN_SUPPORTED_CURRENT_FINANCIAL_YEAR_END_DAY;
}

/**
 * Floor constraint on the current financial year END date. Returns the
 * user-facing message, or null when the value is absent, unparseable, or on /
 * after the floor.
 */
export function checkCurrentEndFloor(endValue: unknown): string | null {
  return isBeforeSupportedFinancialYearEnd(endValue)
    ? UNSUPPORTED_CURRENT_FINANCIAL_YEAR_END_MESSAGE
    : null;
}
